import { Clock, Send, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useDeleteCampaign, useScheduleCampaign, useSendCampaign } from '@/api/hooks/use-campaigns'
import { DateTimePicker } from '@/components/ui/datetime-picker'
import { Modal } from '@/components/ui/modal'

interface Props {
  campaignId: string
  audienceLabel: string
  onBack: () => void
}

export function CampaignStepSend({ campaignId, audienceLabel, onBack }: Props): React.JSX.Element {
  const navigate = useNavigate()
  const sendCampaign = useSendCampaign()
  const scheduleCampaign = useScheduleCampaign()
  const deleteCampaign = useDeleteCampaign()

  const [showSendConfirm, setShowSendConfirm] = useState(false)
  const [showSchedule, setShowSchedule] = useState(false)
  const [showDelete, setShowDelete] = useState(false)
  const [scheduleDate, setScheduleDate] = useState<Date | null>(null)

  function handleSend(): void {
    sendCampaign.mutate(campaignId, {
      onSuccess: () => navigate(`/campaigns/${campaignId}`),
    })
  }

  function handleSchedule(): void {
    if (!scheduleDate) return
    scheduleCampaign.mutate(
      { campaign_id: campaignId, scheduled_at: scheduleDate.toISOString() },
      { onSuccess: () => navigate(`/campaigns/${campaignId}`) },
    )
  }

  function handleDelete(): void {
    deleteCampaign.mutate(campaignId, { onSuccess: () => navigate('/campaigns') })
  }

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <p className="text-sm text-text-secondary">
        Ready to send to <strong>{audienceLabel}</strong>.
      </p>

      {/* Send / Schedule cards */}
      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-xl border border-surface-3 bg-surface-0 p-5 text-center">
          <Send className="mx-auto mb-3 h-8 w-8 text-brand-500" />
          <h3 className="text-sm font-semibold text-text-primary">Send Now</h3>
          <p className="mt-1 text-xs text-text-secondary">Sends immediately to all recipients</p>
          <button
            type="button"
            onClick={() => setShowSendConfirm(true)}
            className="mt-4 w-full rounded-lg bg-brand-500 px-4 py-2.5 text-sm font-medium text-white hover:bg-brand-600"
          >
            Send Campaign
          </button>
        </div>

        <div className="rounded-xl border border-surface-3 bg-surface-0 p-5 text-center">
          <Clock className="mx-auto mb-3 h-8 w-8 text-text-tertiary" />
          <h3 className="text-sm font-semibold text-text-primary">Schedule</h3>
          <p className="mt-1 text-xs text-text-secondary">Pick a date and time to send</p>
          <button
            type="button"
            onClick={() => setShowSchedule(true)}
            className="mt-4 w-full rounded-lg border border-surface-3 px-4 py-2.5 text-sm font-medium text-text-primary hover:bg-surface-1"
          >
            Schedule Send
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between pt-2">
        <button
          type="button"
          onClick={onBack}
          className="rounded-lg border border-surface-3 px-5 py-2.5 text-sm font-medium text-text-primary hover:bg-surface-1"
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={() => setShowDelete(true)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-red-300 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
        >
          <Trash2 className="h-4 w-4" /> Delete Campaign
        </button>
      </div>

      {/* Send confirmation */}
      <Modal
        open={showSendConfirm}
        onClose={() => setShowSendConfirm(false)}
        title="Send Campaign"
        size="sm"
      >
        <p className="text-sm text-text-secondary">
          Send this campaign to <strong>{audienceLabel}</strong>? This cannot be undone.
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setShowSendConfirm(false)}
            className="rounded-lg border border-surface-3 px-4 py-2 text-sm hover:bg-surface-1"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={sendCampaign.isPending}
            className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
          >
            {sendCampaign.isPending ? 'Sending...' : 'Send Now'}
          </button>
        </div>
      </Modal>

      {/* Schedule modal */}
      <Modal open={showSchedule} onClose={() => setShowSchedule(false)} title="Schedule Campaign">
        <div className="space-y-4">
          <DateTimePicker value={scheduleDate} onChange={setScheduleDate} minDate={new Date()} />
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowSchedule(false)}
              className="rounded-lg border border-surface-3 px-4 py-2 text-sm hover:bg-surface-1"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSchedule}
              disabled={!scheduleDate || scheduleCampaign.isPending}
              className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
            >
              {scheduleCampaign.isPending ? 'Scheduling...' : 'Schedule'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete modal */}
      <Modal
        open={showDelete}
        onClose={() => setShowDelete(false)}
        title="Delete Campaign"
        size="sm"
      >
        <p className="text-sm text-text-secondary">Delete this campaign? This cannot be undone.</p>
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setShowDelete(false)}
            className="rounded-lg border border-surface-3 px-4 py-2 text-sm hover:bg-surface-1"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleteCampaign.isPending}
            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            {deleteCampaign.isPending ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </Modal>
    </div>
  )
}
